<script lang="ts" setup>
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { type VMAPathNamesEnum } from '@shared/constants/navigation';
    import { RouterLink, type RouteParamsRaw } from 'vue-router';

    defineProps<{
        mainIcon: string
        secondaryIcon?: string
        pageName: string
        to?: { name: VMAPathNamesEnum, params?: RouteParamsRaw }
        isDisabled?: boolean
        isExpanded?: boolean
        isHighlighted?: boolean
        isActive?: boolean
        isButton?: boolean
        iconSize?: 'small' | 'medium' | 'large'
        tooltipDirection?: 'left' | 'right'
        rounded?: boolean
    }>();
</script>

<template>
    <component
        v-bind="isButton ? {} : { to: to ?? '' }"
        :is="isButton ? 'button' : RouterLink"
        :class="{
            'active': isActive,
            'button': isButton,
            'rounded': rounded
        }"
        :aria-disabled="isDisabled"
        :aria-expanded="isExpanded"
        :aria-highlighted="isHighlighted"
        :disabled="isButton && isDisabled"
        class="navigation-button"
    >
        <Icon
            :name="mainIcon"
            :size="iconSize ?? 'medium'"
            class="navigation-button__icon-main"
        />

        <!-- If no secondary icon given just use main otherwise itll show nothing on hover etc -->
        <Icon
            :name="secondaryIcon ?? mainIcon"
            :size="iconSize ?? 'medium'"
            class="navigation-button__icon-secondary"
        />

        <span
            :class="[`${tooltipDirection === 'right' ? 'show-right' : 'show-left'}`]"
            class="navigation-button__name"
        >
            <strong>{{ pageName }}</strong>
        </span>
    </component>
</template>

<style lang="scss" scoped>
    // eslint-disable-next-line vue-scoped-css/require-selector-used-inside
    .navigation-button,
    // eslint-disable-next-line vue-scoped-css/require-selector-used-inside
    :host .navigation-button {
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        padding-block: 0;
        padding-inline: var(--spacing-03);

        &:not(.button) {
            inline-size: 100%;
        }

        /* stylelint-disable-next-line no-descending-specificity */
        &:focus {
            background-color: var(--background-primary-highlight);
        }

        &.button {
            padding-block: var(--spacing-02);
        }

        &__name {
            display: none;
            white-space: nowrap;

            strong {
                font-size: var(--font-regular);
                text-transform: capitalize;
            }
        }

        // Disabled button styling
        &[aria-disabled=true] {
            opacity: .3;
            pointer-events: none;
        }

        &:hover &__icon-main,
        &:focus &__icon-main,
        &:active &__icon-main,
        &__icon-secondary {
            display: none;
        }

        &:hover &__icon-secondary,
        &:focus &__icon-secondary,
        &:active &__icon-secondary {
            display: flex;
        }

        &.active {
            background: var(--background-primary-highlight);

            .navigation-button__icon-main {
                display: none;
            }

            .navigation-button__icon-secondary {
                display: flex;
            }
        }

        // Only on mobile
        @include max-width(md) {
            &[aria-highlighted=true] {
                position: relative;
                display: flex;
                flex: unset;
                align-items: center;
                justify-content: center;
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-circle);
                background-color: var(--background-primary);
                block-size: var(--size-13);
                inline-size: var(--size-5);
                inset-block-end: 18px;
                padding-block: 0;
                padding-inline: var(--spacing-03);

                &.active {
                    border: 1px solid var(--border-primary);
                    background-color: var(--background-primary-highlight);
                }
            }
        }

        // Tablet and bigger
        @include min-width(md) {
            position: relative;
            flex: unset;

            &__name {
                // eslint-disable-next-line vue-scoped-css/require-selector-used-inside
                [aria-expanded=true] & {
                    display: flex;
                }
            }

            &[aria-expanded=true] {
                justify-content: flex-start;

                .navigation-button__name {
                    margin-inline-start: var(--spacing-02);
                }

                &:hover {
                    .navigation-button__name {
                        text-decoration: underline;
                    }
                }
            }

            &[aria-expanded=false] {
                &:hover {
                    /* stylelint-disable-next-line no-descending-specificity */
                    .navigation-button__name {
                        position: absolute;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 1px solid var(--border-primary);
                        border-radius: var(--border-radius-rounded);
                        background-color: var(--background-primary);
                        block-size: var(--size-7);
                        inset-block-start: 50%;
                        padding-block: 0;
                        padding-inline: var(--spacing-04);
                        transform: translateY(-50%);

                        @include max-width(lg) {
                            display: none;
                        }

                        &.show-left {
                            inset-inline-start: calc(var(--navigation-width) - var(--spacing-02));
                        }

                        &.show-right {
                            inset-inline-end: calc(var(--navigation-width) - var(--spacing-02));
                        }
                    }
                }
            }
        }
    }
</style>
